import { fetchUtils } from 'ra-core'
import simpleRestProvider from 'ra-data-simple-rest'
import {
  CreateParams, DataProvider, DeleteManyParams,
  DeleteParams,
  GetListParams,
  GetManyParams,
  GetManyReferenceParams,
  GetOneParams,
  UpdateManyParams,
  UpdateParams
} from 'react-admin'
import mocks from '../mocks'
import { GetParams } from '../mocks/mockProvider'
import { debug } from '../test'
import authToken from '../utils/authToken'
import { getEnvVariable } from '../utils/getEnvVariable'

/**
 * Convert a `File` object returned by the upload input into a base 64 string.
 * That's not the most optimized way to store images in production, but it's
 * enough to illustrate the idea of data provider decoration.
 */
const convertToBase64 = (blob: { rawFile: Blob }): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob.rawFile)
  })

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const httpClient = (url: string, options: RequestInit = {}): Promise<{
  status: number;
  headers: Headers;
  body: string;
  json: any;
}> => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' })
    options.headers.set('accept-language', 'nl')
  }

  const user = {
    authenticated: true,
    token: authToken.getToken() || '',
  }

  if (!user.token || user.token === '') return Promise.reject({ status: 412 })

  return fetchUtils.fetchJson(url, { ...options, user })
}

export const getUrl = (): string => getEnvVariable('REACT_APP_SERVER_URL', '')

export const getJson = async <T,>(url: string, options?: RequestInit): Promise<T> => {
  const response = await httpClient(url, options)
  return response.json as T
}

export const putJson = async <T,>(url: string, body: unknown): Promise<T> => {
  const response = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'accept-language': 'nl',
    }),
  })
  return response.json as T
}

export const postJson = async <T,>(url: string, body?: unknown): Promise<T> => {
  const response = await httpClient(url, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
    headers: new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'accept-language': 'nl',
    }),
  })
  return response.json as T
}
debug && console.log({ getUrl: getUrl() })


const dataProvider: DataProvider = simpleRestProvider(getUrl(), httpClient)

interface ObjectionsParams {
  id: string;
  municipalityId: number
  file: {
    rawFile: File
    src?: string
    title?: string
  }
  status: string;
}

const createPostFormData = (
  params: CreateParams<ObjectionsParams> | UpdateParams<ObjectionsParams>
) => {
  const formData = new FormData()
  params.data.file?.rawFile && formData.append('file', params.data.file.rawFile)
  params.data.municipalityId && formData.append('municipalityId', `${params.data.municipalityId}`)

  return formData
}

const createDocumentQueryString = (data: Record<string, unknown>): string => {
  const queryParams = new URLSearchParams()
  const queryKeys = [
    'documentOmschrijving',
    'nummerBinnenSysteem',
    'kenmerkSysteem',
    'vertrouwelijkheidsAanduiding',
    'taal',
    'syncToZaken',
  ]

  queryKeys.forEach(key => {
    // eslint-disable-next-line security/detect-object-injection
    const value = data?.[key]
    if (value !== undefined && value !== null && value !== '') {
      queryParams.set(key, String(value))
    }
  })

  const queryString = queryParams.toString()
  return queryString ? `?${queryString}` : ''
}

interface ObjectionEditParams {
  id: string
  files: {
    rawFile: File
    src?: string
    title?: string
  }[]
  municipalityId?: string
}

const editPostFormData = (
  params: CreateParams<ObjectionEditParams> | UpdateParams<ObjectionEditParams>
) => {
  const formData = new FormData()

  if (params.data.files && Array.isArray(params.data.files)) {
    params.data.files.forEach((file) => {
      if (file.rawFile) {
        formData.append('files', file.rawFile)
        formData.append('original_filenames', file.rawFile.name)
      }
    })
  }
  if (params.data.municipalityId) {
    formData.append('municipalityId', params.data.municipalityId)
  }

  return formData
}

interface CreateManyParams {
  data: Record<string, unknown>[]
}

export const xxllncDataProvider: DataProvider = {
  ...dataProvider,
  get: async (resource: string) => httpClient(`${getUrl()}/${resource}`, {
    method: 'GET',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  }).then(({ json }) => ({ data: json })),
  getList: (resource: string, params: GetListParams) => {
    if (resource === 'gebiedsprognoses') {
      return dataProvider.getList('prognose_sets', {
        ...params,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        filter: { ...params.filter, type: 'gebied' },
      })
    }
    // Custom pages, not REST resources
    if (
      resource === 'historische_data' ||
      resource === 'historische_data_stad' ||
      resource === 'historische_data_gebied' ||
      resource === 'overige_data' ||
      resource === 'overige_data_stad' ||
      resource === 'overige_data_gebied'
    ) {
      return Promise.resolve({ data: [], total: 0 })
    }
    return dataProvider.getList(resource, params)
  },
  getOne: (resource: string, params: GetOneParams) => {
    if (resource === 'gebiedsprognoses') {
      return dataProvider.getOne('prognose_sets', params)
    }
    return dataProvider.getOne(resource, params)
  },
  create: (resource: string, params: CreateParams) => {
    if (resource.includes('documenten')) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const formData = createPostFormData(params)
      const queryString = createDocumentQueryString(params.data)

      return httpClient(`${getUrl()}/${resource}${queryString}`, {
        method: 'POST',
        body: formData,
      })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        .then(({ json }) => ({ data: json }))
    }
    if (resource === 'uploads') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const formData = editPostFormData(params)
      return httpClient(`${getUrl()}/${resource}`, {
        method: 'POST',
        body: formData,
      })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        .then(({ json }) => ({ data: json }))
    }

    if (!params.data.icon) {
      return dataProvider.create(resource, params)
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!(params.data.icon.rawFile instanceof File)) {
      throw new Error('The rawFile is not a File object.')
    }

    return convertToBase64(params.data.icon).then((icon) => dataProvider.create(resource, {
      ...params,
      data: {
        ...params.data,
        icon: icon.replace('data:image/svg+xml;base64,', ''),
      },
    }))
  },
  createMany: async (resource: string, params: CreateManyParams) => {
    debug && console.log('createMany called successfully', { resource, params })
    if (resource === 'organization') {
      return httpClient(`${getUrl()}/${resource}/createMany`, {
        method: 'POST',
        body: JSON.stringify({
          data: params.data.map(item => ({
            name: item.Naam,
            integrationKey: item.uuid,
            integrationType: 'crm',
          }))
        }),
      })
    }

    if (resource === 'app_instance') {
      return httpClient(`${getUrl()}/${resource}/createMany`, {
        method: 'POST',
        body: JSON.stringify({
          data: params.data.map(item => {
            // eslint-disable-next-line camelcase
            const { appId, app_id, organizationId, organization_id, hostnames, ...csv } = item
            csv.appId = app_id || appId || null // eslint-disable-line camelcase
            csv.organizationId = organization_id || organizationId || null // eslint-disable-line camelcase
            csv.hostnames = String(hostnames).split(' ')
            return ({ ...csv })
          })
        }),
      })
    }

    return params.data.map(item => dataProvider.create(resource, {
      data: {
        ...item
      }
    }))
  },
  update: (resource: string, params: UpdateParams) => {
    if (resource === 'objection' && !!params.data.files) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const formData = editPostFormData(params)
      return httpClient(`${getUrl()}/${resource}/${params.data.id}/documents`, {
        method: 'PUT',
        body: formData,
      })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        .then(({ json }) => ({ data: json }))
    }
    if (params.data.icon && typeof params.data.icon != 'string') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (params.data.icon.rawFile && !(params.data.icon.rawFile instanceof File)) {
        throw new Error('The rawFile is not a File object.')
      }

      return convertToBase64(params.data.icon).then((icon) => dataProvider.update(resource, {
        ...params,
        data: {
          ...params.data,
          icon: icon.replace('data:image/svg+xml;base64,', ''),
        },
      }))
    }

    return dataProvider.update(resource, params)
  },
}

export const mockDataProvider: DataProvider = {
  get: async (resource: string, params: GetParams) => mocks.mockProvider.get(resource, params),
  getList: async (resource: string, params: GetListParams) => mocks.mockProvider.getList(resource, params),
  getOne: (resource: string, params: GetOneParams) => mocks.mockProvider.getOne(resource, params),
  getMany: (resource: string, params: GetManyParams) => mocks.mockProvider.getMany(resource, params),
  create: (resource: string, params: CreateParams) => mocks.mockProvider.create(resource, params),
  update: (resource: string, params: UpdateParams) => mocks.mockProvider.update(resource, params),
  delete: (resource: string, params: DeleteParams) => mocks.mockProvider.delete(resource, params),
  getManyReference: (resource: string, params: GetManyReferenceParams) => mocks.mockProvider.getManyReference(resource, params),
  /* Below is not implemented yet */
  updateMany: (resource: string, params: UpdateManyParams) =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    httpClient(`xxx/${resource}/${JSON.stringify(params.ids)}`).then(response => ({ data: response.json })),
  deleteMany: (resource: string, params: DeleteManyParams) =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    httpClient(`xxx/${resource}/${JSON.stringify(params.ids)}`).then(response => ({ data: response.json }))
}
